/**
 * GPU-Composited Multi-App Smart Sticky Sidebar Controller
 *
 * Implements a flawless, completely stable, and jitter-free smart sticky sidebar.
 * Optimized to handle both window-scrolling and container-scrolling contexts.
 *
 * Uses position: fixed only for the main window sticky “stuck” edges. The suggestions
 * dropdown mirrors the same smart sticky rules as CASE B but applies them with translate3d
 * only (never fixed inside the transformed scrolling root — that caused disappearance).
 * Fully prevents parent layout collapses and element disappearance bugs under all
 * states by locking the parent sidebar height to the active app content height,
 * while completely breaking circular height dependencies during window resizes.
 *
 * Bypasses native CSS position: sticky inside transformed containers where the W3C
 * specification disables sticky positioning and forces it to behave as relative.
 *
 * @since   2.0.0
 * @package SnapFind
 */

/**
 * Calculates the absolute static top coordinate of an element relative to the document.
 *
 * @param {HTMLElement} element
 * @returns {number}
 */
function getAbsoluteDocumentTop(element) {
    let top = 0;
    let el = element;
    while (el) {
        top += el.offsetTop || 0;
        el = el.offsetParent;
    }
    return top;
}

/**
 * Y distance from the scroll container's content origin to the target's top edge.
 * Robust for flex/grid (offsetTop chains are unreliable vs scrollTop).
 *
 * @param {HTMLElement} scrollRoot
 * @param {HTMLElement} target
 * @returns {number}
 */
function getScrollContentOffsetTop(scrollRoot, target) {
    const rootRect = scrollRoot.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    return scrollRoot.scrollTop + (targetRect.top - rootRect.top);
}

export function initStickySidebar() {
    const apps = document.querySelectorAll('.snapfind-app');

    apps.forEach((app) => {
        const sidebar = app.querySelector('.snapfind-sidebar');
        if (!sidebar) return;

        const isSuggestions = app.classList.contains('type-suggestions');

        // Dynamically wrap sidebar contents in an inner container if not already present
        let inner = sidebar.querySelector('.snapfind-sidebar-inner');
        if (!inner) {
            inner = document.createElement('div');
            inner.className = 'snapfind-sidebar-inner';
            while (sidebar.firstChild) {
                inner.appendChild(sidebar.firstChild);
            }
            sidebar.appendChild(inner);
        }

        // Configure stable relative positioning base
        app.style.position = 'relative';
        sidebar.style.position = 'relative';

        // Resolve the scroll container box
        const scrollContainer = isSuggestions
            ? app.querySelector('.snaf-suggestions-dropdown')
            : window;

        if (!scrollContainer) return;

        let lastScrollTop =
            scrollContainer === window
                ? window.pageYOffset || document.documentElement.scrollTop
                : scrollContainer.scrollTop;

        let currentTranslateY = 0;

        let suggestionsScrollRaf = 0;
        /** Avoid rewriting inner width unless it changes — reduces tiny layout jitter while scrolling */
        let suggestionsInnerWidthApplied = '';

        const flushStickyFrame = () => {
            if (suggestionsScrollRaf) {
                cancelAnimationFrame(suggestionsScrollRaf);
                suggestionsScrollRaf = 0;
            }
        };

        const updateSticky = () => {
            const viewportWidth = window.innerWidth;

            // Disable sticky behavior on mobile/tablets where layout flows vertically
            if (viewportWidth <= 860) {
                flushStickyFrame();
                suggestionsInnerWidthApplied = '';
                sidebar.style.position = '';
                sidebar.style.top = '';
                sidebar.style.height = '';
                sidebar.style.minHeight = '';
                inner.style.position = '';
                inner.style.top = '';
                inner.style.bottom = '';
                inner.style.left = '';
                inner.style.width = '';
                inner.style.transform = '';
                inner.style.willChange = '';
                currentTranslateY = 0;
                return;
            }

            // SANITIZE STATE RESET:
            // When the suggestions modal is closed (offsetWidth is 0), reset its
            // scroll memory offsets to 0. This ensures reopening starts fresh.
            if (isSuggestions && scrollContainer.offsetWidth === 0) {
                currentTranslateY = 0;
                lastScrollTop = 0;
                flushStickyFrame();
                suggestionsInnerWidthApplied = '';
                sidebar.style.height = '';
                sidebar.style.minHeight = '';
                inner.style.transform = '';
                inner.style.willChange = '';
                return;
            }

            // (Circular height dependencies are bypassed by querying the sibling main content area directly)

            // Read scroll positions based on the scroll container context
            const scrollTop =
                scrollContainer === window
                    ? window.pageYOffset || document.documentElement.scrollTop
                    : scrollContainer.scrollTop;

            const viewportHeight =
                scrollContainer === window ? window.innerHeight : scrollContainer.clientHeight;

            // Detect WordPress admin bar to offset sticky margins dynamically
            const adminBar = document.getElementById('wpadminbar');
            const adminBarHeight =
                adminBar && window.getComputedStyle(adminBar).display !== 'none'
                    ? adminBar.offsetHeight
                    : 0;

            const topMargin = scrollContainer === window ? adminBarHeight + 24 : 20;
            const bottomMargin = 20;

            // Read active layout dimensions from the main content container to avoid circular
            // height dependencies with the sticky sidebar, preventing layout shifts on scroll.
            const mainContent = app.querySelector('.snapfind-main');
            let appTop = 0;
            let appHeight = 0;

            if (scrollContainer === window) {
                appTop = getAbsoluteDocumentTop(app);
                appHeight = mainContent ? mainContent.offsetHeight : app.offsetHeight;
            } else {
                const body = app.querySelector('.snaf-full-mode-body');
                appHeight = mainContent
                    ? mainContent.offsetHeight
                    : body
                      ? body.offsetHeight
                      : scrollContainer.scrollHeight;
            }

            const sidebarHeight = inner.offsetHeight;

            // Calculate active scrollable space bounds
            const maxTranslateY = Math.max(0, appHeight - sidebarHeight);

            // CASE A: Shorter Sidebar Native Sticky Fallback
            // If the sidebar fits fully in the viewport, let the browser handle sticking natively.
            // CRITICAL: We only run this fallback on the main window page. Inside the suggestions modal,
            // the transform style on the modal disables native CSS position: sticky (forcing it to act as relative).
            // Inside the suggestions modal, Case B uses absolute positioning only (see apply block below).
            if (
                scrollContainer === window &&
                sidebarHeight + topMargin + bottomMargin < viewportHeight
            ) {
                sidebar.style.position = 'sticky';
                sidebar.style.top = `${topMargin}px`;
                sidebar.style.height = '';
                sidebar.style.minHeight = '';
                inner.style.position = '';
                inner.style.top = '';
                inner.style.bottom = '';
                inner.style.left = '';
                inner.style.width = '';
                inner.style.transform = '';
                inner.style.willChange = '';
                currentTranslateY = 0;
                lastScrollTop = scrollTop;
                return;
            }

            // CASE B: Tall Sidebar / Transformed Modal Smart Scrolling Mechanics
            // Clear sticky layout parameters from the outer sidebar container
            sidebar.style.position = 'relative';
            sidebar.style.top = '';

            // Lock the outer sidebar height to the true, natural appHeight.
            // This prevents the parent container from collapsing (and the sidebar from disappearing)
            // under absolutely positioned inner content and gives tall sidebars enough track to clamp.
            // Use the maximum of appHeight and sidebarHeight to handle short product grids.
            sidebar.style.height = `${Math.max(appHeight, sidebarHeight)}px`;

            const sidebarRect = sidebar.getBoundingClientRect();
            const sidebarWidth = sidebarRect.width;
            const sidebarLeft = sidebarRect.left;

            // Suggestions dropdown: same smart sticky as window CASE B (directional follow /
            // pinch top & bottom), composited via translate3d — never fixed (transform parent).
            if (scrollContainer !== window) {
                const sidebarTrackTop = getScrollContentOffsetTop(scrollContainer, sidebar);

                inner.style.position = 'absolute';
                inner.style.top = '0';
                inner.style.bottom = 'auto';
                inner.style.right = 'auto';
                inner.style.left = '0';
                inner.style.willChange = 'transform';

                const nextInnerW = `${Math.max(0, Math.round(sidebarWidth))}px`;
                if (nextInnerW !== suggestionsInnerWidthApplied) {
                    inner.style.width = nextInnerW;
                    suggestionsInnerWidthApplied = nextInnerW;
                }

                const applyModalTranslate = (yPx) => {
                    const pinned = Math.max(0, Math.min(maxTranslateY, Math.round(yPx)));
                    inner.style.transform = `translate3d(0, ${pinned}px, 0)`;
                    currentTranslateY = pinned;
                };

                const viewportTopRel = scrollTop - sidebarTrackTop + topMargin;
                const viewportBottomRel =
                    scrollTop - sidebarTrackTop + viewportHeight - bottomMargin;

                if (scrollTop === lastScrollTop) {
                    currentTranslateY = Math.max(0, Math.min(currentTranslateY, maxTranslateY));
                    applyModalTranslate(currentTranslateY);
                    return;
                }

                const scrollingDown = scrollTop > lastScrollTop;
                const isShorter = sidebarHeight + topMargin + bottomMargin < viewportHeight;

                let newTranslateY = currentTranslateY;

                if (isShorter) {
                    newTranslateY = viewportTopRel;
                    newTranslateY = Math.max(0, Math.min(newTranslateY, maxTranslateY));
                } else if (scrollingDown) {
                    const targetTranslateY = viewportBottomRel - sidebarHeight;
                    newTranslateY = Math.max(currentTranslateY, targetTranslateY);
                    newTranslateY = Math.min(newTranslateY, maxTranslateY);
                } else {
                    const targetTranslateY = viewportTopRel;
                    newTranslateY = Math.min(currentTranslateY, targetTranslateY);
                    newTranslateY = Math.max(0, newTranslateY);
                }

                applyModalTranslate(newTranslateY);
                lastScrollTop = scrollTop;
                return;
            }

            inner.style.transform = '';
            inner.style.willChange = '';

            // Helper to write positioning styles cleanly (window CASE B only)
            const setInnerStyles = (position, top, bottom, left) => {
                inner.style.position = position;
                inner.style.top = top;
                inner.style.bottom = bottom;
                inner.style.left = left;
                inner.style.width = `${sidebarWidth}px`;
            };

            // Clamp coordinate calculations if scrolling hasn't actually occurred
            if (scrollTop === lastScrollTop) {
                currentTranslateY = Math.max(0, Math.min(currentTranslateY, maxTranslateY));
                setInnerStyles('absolute', `${currentTranslateY}px`, 'auto', '0');
                return;
            }

            // Determine scroll direction
            const scrollingDown = scrollTop > lastScrollTop;

            // Viewport boundaries relative to app container
            const viewportTopRel = scrollTop - appTop + topMargin;
            const viewportBottomRel = scrollTop - appTop + viewportHeight - bottomMargin;

            // Determine if the sidebar is shorter than the viewport
            const isShorter = sidebarHeight + topMargin + bottomMargin < viewportHeight;

            let newTranslateY = currentTranslateY;

            if (isShorter) {
                newTranslateY = viewportTopRel;
                newTranslateY = Math.max(0, Math.min(newTranslateY, maxTranslateY));
            } else if (scrollingDown) {
                const targetTranslateY = viewportBottomRel - sidebarHeight;
                newTranslateY = Math.max(currentTranslateY, targetTranslateY);
                newTranslateY = Math.min(newTranslateY, maxTranslateY);
            } else {
                const targetTranslateY = viewportTopRel;
                newTranslateY = Math.min(currentTranslateY, targetTranslateY);
                newTranslateY = Math.max(0, newTranslateY);
            }

            // Apply optimized position offsets
            if (newTranslateY <= 0) {
                setInnerStyles('absolute', '0px', 'auto', '0');
            } else if (newTranslateY >= maxTranslateY) {
                setInnerStyles('absolute', `${maxTranslateY}px`, 'auto', '0');
            } else {
                const eps = 1;
                if (Math.abs(newTranslateY - viewportTopRel) < eps) {
                    setInnerStyles('fixed', `${topMargin}px`, 'auto', `${sidebarLeft}px`);
                } else if (Math.abs(newTranslateY - (viewportBottomRel - sidebarHeight)) < eps) {
                    setInnerStyles('fixed', 'auto', `${bottomMargin}px`, `${sidebarLeft}px`);
                } else {
                    setInnerStyles('absolute', `${newTranslateY}px`, 'auto', '0');
                }
            }

            currentTranslateY = newTranslateY;
            lastScrollTop = scrollTop;
        };

        const handleResize = () => {
            flushStickyFrame();
            suggestionsInnerWidthApplied = '';
            updateSticky();
        };

        const handleScroll = () => {
            if (scrollContainer === window) {
                updateSticky();
                return;
            }
            if (suggestionsScrollRaf) return;
            suggestionsScrollRaf = requestAnimationFrame(() => {
                suggestionsScrollRaf = 0;
                updateSticky();
            });
        };

        const eventTarget = scrollContainer === window ? window : scrollContainer;
        eventTarget.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleResize, { passive: true });

        // Initial render run (sync; ensure layout before dropdown rAF backlog)
        flushStickyFrame();
        updateSticky();

        document.addEventListener('snapfind:updated', () => {
            flushStickyFrame();
            suggestionsInnerWidthApplied = '';
            updateSticky();
        });
    });
}
